import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Play, Pause, Maximize2 } from 'lucide-react';
import { useAutomaton } from '../state/AutomatonContext';
import { CellGrid } from '../components/CellGrid';
import { CellDetailPanel } from '../components/CellDetailPanel';

const FILTERS = ['All', 'Alive only', 'Thriving', 'Dying', 'My stakes', 'Deepest lineages'];
const LEGEND: Array<{ label: string; color: string }> = [
  { label: 'alive', color: '#1E6FFF' },
  { label: 'thriving', color: '#00FF88' },
  { label: 'low', color: '#FFB800' },
  { label: 'critical', color: '#FF3344' },
  { label: 'dead', color: '#2A2A2A' },
];

export function Grid() {
  const { cells, paused, pause, resume, tickMs, setSpeed, addStake } = useAutomaton();
  const [params, setParams] = useSearchParams();
  const initialId = params.get('cell') ? parseInt(params.get('cell')!, 10) : null;
  const [selectedId, setSelectedId] = useState<number | null>(initialId);
  const [filter, setFilter] = useState('All');
  const [sheetOpen, setSheetOpen] = useState(false);

  const selectedCell = selectedId != null ? cells[selectedId] ?? null : null;

  useEffect(() => {
    if (selectedId != null) {
      setSheetOpen(true);
    }
  }, [selectedId]);

  function handleSelect(id: number) {
    setSelectedId(id);
    setParams({ cell: String(id) });
  }

  function toggleFullscreen() {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen?.();
    } else {
      document.exitFullscreen?.();
    }
  }

  return (
    <div className="max-w-[1600px] mx-auto px-3 sm:px-6 py-4 sm:py-6">
      <div className="grid gap-4 lg:grid-cols-[1fr_360px]">
        <div className="space-y-3">
          <div className="aspect-square w-full max-h-[80vh] bg-void hairline">
            <CellGrid cells={cells} selectedId={selectedId} onSelect={handleSelect} filter={filter} />
          </div>

          <div className="hairline bg-panel px-3 py-3 flex flex-wrap items-center gap-3 text-2xs uppercase tracking-tightest">
            <button
              onClick={paused ? resume : pause}
              className="inline-flex items-center gap-1 px-3 py-1 border border-blue-life text-blue-life rounded-sm hover:bg-blue-life/10"
            >
              {paused ? <Play size={12} /> : <Pause size={12} />}
              {paused ? 'PAUSED' : 'LIVE'}
            </button>

            <div className="flex items-center gap-2">
              <span className="text-text-mono-dim">TICK</span>
              <input
                type="range"
                min={500}
                max={10000}
                step={500}
                value={tickMs}
                onChange={(e) => setSpeed(parseInt(e.target.value, 10))}
                className="accent-blue-life w-24 sm:w-32"
              />
              <span className="font-mono text-text-secondary">{(tickMs / 1000).toFixed(1)}s</span>
            </div>

            <label className="flex items-center gap-2">
              <span className="text-text-mono-dim">FILTER</span>
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="hairline bg-void text-text-primary px-2 py-1"
              >
                {FILTERS.map((f) => (
                  <option key={f} value={f}>
                    {f}
                  </option>
                ))}
              </select>
            </label>

            <div className="flex items-center gap-2 ml-auto">
              {LEGEND.map((l) => (
                <span key={l.label} className="flex items-center gap-1 text-text-mono-dim">
                  <span className="inline-block w-2 h-2" style={{ background: l.color }} />
                  {l.label}
                </span>
              ))}
              <button
                onClick={toggleFullscreen}
                aria-label="Fullscreen"
                className="text-text-secondary hover:text-text-primary p-1"
              >
                <Maximize2 size={14} />
              </button>
            </div>
          </div>
        </div>

        <aside className="hidden lg:block hairline bg-panel max-h-[80vh]">
          <CellDetailPanel cell={selectedCell} onStaked={(cellId, amt) => addStake(cellId, amt)} />
        </aside>
      </div>

      {/* Mobile bottom sheet */}
      {sheetOpen && selectedCell && (
        <div className="lg:hidden fixed inset-x-0 bottom-0 z-40 max-h-[60vh] bg-panel hairline border-b-0 overflow-y-auto">
          <div className="flex items-center justify-between px-4 py-2 border-b border-grid-line">
            <span className="text-2xs text-text-mono-dim font-mono">CELL DETAIL</span>
            <button
              onClick={() => setSheetOpen(false)}
              className="text-text-secondary text-xs"
            >
              close ✕
            </button>
          </div>
          <CellDetailPanel cell={selectedCell} onStaked={(cellId, amt) => addStake(cellId, amt)} />
        </div>
      )}
    </div>
  );
}
